<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, url, minLength } from '@vuelidate/validators';
import wootConstants from 'dashboard/constants/globals';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import { useConfig } from 'dashboard/composables/useConfig';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { EXAMPLE_WEBHOOK_URL } = wootConstants;

const SUPPORTED_WEBHOOK_EVENTS = [
  'conversation_created',
  'conversation_status_changed',
  'conversation_updated',
  'conversation_resolved',
  'message_created',
  'message_updated',
  'webwidget_triggered',
  'contact_created',
  'contact_updated',
  'conversation_typing_on',
  'conversation_typing_off',
];

export default {
  components: {
    NextButton,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['submit', 'cancel'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    url: {
      required,
      minLength: minLength(7),
      url,
    },
    subscriptions: {
      required,
    },
  },
  data() {
    const { inboxEventsEnabled } = useConfig();
    return {
      url: this.value.url || '',
      name: this.value.name || '',
      /**
       * Bloco 4.13 — o webhook passou a ser entregue SO para a inbox escolhida, e ate
       * aqui nao havia como escolher: o campo existia no banco e no controller e nao
       * existia na tela. Vazio = todas as inboxes, que e o estado de quem nunca preencheu
       * o campo — e continua sendo o padrao de um webhook novo.
       *
       * `value.inbox` (e nao `value.inbox_id`) porque e isso que o `index` devolve: o
       * partial serializa a inbox como objeto aninhado com id e nome.
       */
      inboxId: this.value.inbox?.id || '',
      subscriptions: this.value.subscriptions || [],
      supportedWebhookEvents: inboxEventsEnabled
        ? [...SUPPORTED_WEBHOOK_EVENTS, 'inbox_updated']
        : SUPPORTED_WEBHOOK_EVENTS,
    };
  },
  computed: {
    ...mapGetters({ inboxes: 'inboxes/getInboxes' }),
    /**
     * Bloco 4.14. Lia `value.secret`, e isso estava errado nos DOIS sentidos:
     *
     *  · logo depois de criar, o registro que o store guarda ainda carrega o segredo (a
     *    criacao o devolve uma vez), entao o formulario o DESENHAVA — o vazamento;
     *  · depois de um F5, `GET /webhooks` nao devolve segredo nenhum, entao este bloco
     *    sumia inteiro e o texto "voce tambem podera encontra-lo depois no formulario de
     *    edicao" virava mentira.
     *
     * `has_secret` e o campo que o `index` devolve de proposito (`_webhook.json.jbuilder`):
     * diz que EXISTE um segredo, sem dizer qual.
     */
    hasSecret() {
      return !!this.value.has_secret;
    },
    webhookURLInputPlaceholder() {
      return this.$t(
        'INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.PLACEHOLDER',
        {
          webhookExampleURL: EXAMPLE_WEBHOOK_URL,
        }
      );
    },
    webhookNameInputPlaceholder() {
      return this.$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.PLACEHOLDER');
    },
  },
  methods: {
    onSubmit() {
      this.$emit('submit', {
        url: this.url,
        name: this.name,
        // `null`, e nao `''`: "todas as inboxes" e a opcao de valor vazio, e mandar `''`
        // deixaria o NULL por conta do cast do Rails. `null` diz o que se quer dizer —
        // este webhook nao esta preso a inbox nenhuma. (Medido: essa e a conversao que
        // pesa; o `Number` e redundante hoje, porque o `:value` do `<option>` ja carrega
        // o id como numero, e existe para o dia em que ele deixar de carregar.)
        inbox_id: this.inboxId === '' ? null : Number(this.inboxId),
        subscriptions: this.subscriptions,
      });
    },
    getI18nKey,
  },
};
</script>

<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div class="w-full">
      <label :class="{ error: v$.url.$error }">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.LABEL') }}
        <input
          v-model="url"
          type="text"
          name="url"
          :placeholder="webhookURLInputPlaceholder"
          @input="v$.url.$touch"
        />
        <span v-if="v$.url.$error" class="message">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.ERROR') }}
        </span>
      </label>
      <label>
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.NAME.LABEL') }}
        <input
          v-model="name"
          type="text"
          name="name"
          :placeholder="webhookNameInputPlaceholder"
        />
      </label>
      <!--
        Bloco 4.14 — o segredo NAO e desenhado aqui, em nenhum estado. Nao ha `v-if` que o
        mostre, nao ha botao de "revelar" e nao ha copia: o que existe e a AFIRMACAO de que
        um segredo esta configurado. O valor so aparece na criacao, uma vez, que e o unico
        momento em que ele existe do lado de fora do banco.
      -->
      <label v-if="hasSecret" class="mb-4">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.LABEL') }}
        <div class="flex items-center gap-2">
          <input
            value="••••••••••••••••••••••••••••••••"
            type="text"
            readonly
            disabled
            class="!mb-0 font-mono"
          />
        </div>
        <span class="text-xs text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SECRET.CONFIGURED_DESC') }}
        </span>
      </label>
      <!--
        Bloco 4.13 — o seletor de inbox. Sem ele o filtro por inbox era inalcancavel pela
        tela: so quem chamasse a API na mao conseguia prender um webhook a uma inbox.
      -->
      <label for="webhook-inbox">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOX.LABEL') }}
        <select id="webhook-inbox" v-model="inboxId" name="inbox_id">
          <option value="">
            {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOX.ALL') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
        <span class="text-xs text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.INBOX.HELP') }}
        </span>
      </label>
      <label :class="{ error: v$.url.$error }" class="mb-2 mt-4">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.LABEL') }}
      </label>
      <div class="flex flex-col gap-2.5 mb-4">
        <div
          v-for="event in supportedWebhookEvents"
          :key="event"
          class="flex items-center"
        >
          <input
            :id="event"
            v-model="subscriptions"
            type="checkbox"
            :value="event"
            name="subscriptions"
            class="mr-2"
          />
          <label :for="event" class="text-sm">
            {{
              `${$t(
                getI18nKey(
                  'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
                  event
                )
              )} (${event})`
            }}
          </label>
        </div>
      </div>
    </div>

    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
        @click.prevent="$emit('cancel')"
      />
      <NextButton
        type="submit"
        :disabled="v$.$invalid || isSubmitting"
        :is-loading="isSubmitting"
        :label="submitLabel"
      />
    </div>
  </form>
</template>
